// Section 19 and 14: Stranger with a story. 3% per season, any season; more often above trust 75,
// half as often under founders-first, never at the population cap. One adult who says they are a
// healer: 60% of the time a skilled healer (3 to 4.5), 40% a thief who steals food and tools when
// admitted and slips away. Both claim the same skill, so they cannot be told apart this season.
// Choices: Admit or Refuse (refusing is turning away, Section 14).
// Constants: config.events.stranger, config.crime.theftFood/theftTools (the amounts stolen).
import type { Config } from '../../config';
import {
  arrivalChoices,
  arrivalRoom,
  arrivalWeight,
  choiceOutcome,
  choiceRng,
  eventEntry,
  eventRng,
} from '../../core/content-helpers';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { ReadContext } from '../../core/types';

interface Plan {
  healer: boolean;
  claim: number;
  seed: number;
}

export function stranger(config: Config) {
  const c = config.events.stranger;
  const plan = (ctx: ReadContext): Plan => {
    const rng = eventRng(ctx, 'stranger', 'plan');
    const healer = rng.float() < c.healerChance;
    const claim =
      Math.round((c.healerSkillMin + rng.float() * (c.healerSkillMax - c.healerSkillMin)) * 10) /
      10;
    return { healer, claim, seed: Math.floor(rng.float() * 4294967296) >>> 0 };
  };
  const admit: EventChoiceDef = {
    id: 'admit',
    label: 'Admit the stranger',
    preview: () => [{ key: 'stranger-admit', params: {} }],
    apply: (ctx, prompt) => {
      const group = prompt.groupId === null ? undefined : ctx.state.arrivalGroups[prompt.groupId];
      const person = group?.candidates[0];
      if (group === undefined || person === undefined) return [];
      const gate = {
        kind: 'gate' as const,
        groupId: group.id,
        decision: 'admit-all' as const,
        personIds: [],
        source: 'event:stranger',
      };
      if ((person.skills.healer ?? 0) >= c.healerSkillMin)
        return [gate, choiceOutcome(prompt, 'admit', { variant: 'healer', name: person.name })];
      const rng = choiceRng(prompt, 'theft');
      const theft = ctx.cfg.crime;
      const food = Math.min(
        ctx.state.stocks.food.amount,
        rng.int(theft.theftFood.min, theft.theftFood.max),
      );
      const tools = Math.min(
        ctx.state.stocks.tools.amount,
        rng.int(theft.theftTools.min, theft.theftTools.max),
      );
      return [
        { kind: 'stock', resource: 'food', amount: -food, flow: 'theft' },
        { kind: 'stock', resource: 'tools', amount: -tools, flow: 'theft' },
        { ...gate, vanish: true },
        choiceOutcome(prompt, 'admit', { variant: 'thief', name: person.name, food, tools }),
      ];
    },
  };
  const refuse = arrivalChoices('stranger', {
    'turn-away': { id: 'refuse', label: 'Refuse' },
  });
  return defineEvent({
    id: 'stranger',
    label: 'Stranger with a story',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    foreshadow: null,
    ledgerTag: 'gate',
    weight: (ctx) => arrivalWeight(ctx, c.baseChance),
    text: () => eventEntry('stranger'),
    apply: (ctx) => {
      if (arrivalRoom(ctx) <= 0) return [];
      const p = plan(ctx);
      return [
        {
          kind: 'arrivals',
          spec: {
            eventId: 'stranger',
            reason: 'stranger',
            size: 1,
            composition: 'adults',
            skilled: p.healer
              ? { skill: 'healer', min: c.healerSkillMin, max: c.healerSkillMax }
              : null,
            claims: { healer: p.claim },
            seed: p.seed,
          },
        },
      ];
    },
    choices: [admit, ...refuse],
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
